import React from 'react';
import { useQuery } from 'react-apollo';
import { t, defineMessages } from '@sm/intl';
import { createURL } from '@sm/utils';
import { TopBarSlim } from '../../TopBarSlim';
import { UseCaseVariationEnums } from '../../TopBarSlimEnums';
import useCaseOtherTopBarSlimQuery from './UseCaseOtherTopBarSlim.graphql';
import TopBarSlimSkeleton from '../../TopBarSlimSkeleton';
import { EmptyComponent } from '../../../helpers/EmptyComponent';

const DEFAULT_COPY = defineMessages({
  MESSAGE: {
    id: 'UseCaseOtherTopBarSlim.message',
    defaultMessage:
      "Don't limit yourself. Ask more than {createQuestionLimit, plural, one { 1 question } other {{ createQuestionLimit } questions}} and get all the answers you need.",
    description: '[Type: Paragraph][Vis.: low] - Top bar slim message',
  },
  CTA_TEXT: {
    id: 'UseCaseOtherTopBarSlim.ctaText',
    defaultMessage: 'See all plans',
    description: '[Type: Button][Vis.: low] - Top bar slim cta text',
  },
});

const COLLECT_COPY = defineMessages({
  MESSAGE: {
    id: 'UseCaseOtherTopBarSlim.messageCollect',
    defaultMessage:
      'Hear from more than {analyzeResponseLimit, plural, one { 1 person } other {{ analyzeResponseLimit } people}}. Capture answers from all survey-takers.',
    description: '[Type: Paragraph][Vis.: low] - Top bar slim message',
  },
  CTA_TEXT: {
    id: 'UseCaseOtherTopBarSlim.ctaTextCollect',
    defaultMessage: 'Get unlimited responses',
    description: '[Type: Button][Vis.: low] - Top bar slim cta text',
  },
});

const ANALYZE_COPY = defineMessages({
  MESSAGE: {
    id: 'UseCaseOtherTopBarSlim.messageAnalyze',
    defaultMessage:
      'Make more sense of your survey results with advanced export options.',
    description: '[Type: Paragraph][Vis.: low] - Top bar slim message',
  },
  CTA_TEXT: {
    id: 'UseCaseOtherTopBarSlim.ctaTextAnalyze',
    defaultMessage: 'Unlock exports',
    description: '[Type: Button][Vis.: low] - Top bar slim cta text',
  },
});

export type UseCaseOtherTopBarSlimProps = {
  showLoaderOnFetch?: boolean;
  variation?: UseCaseVariationEnums | null;
};

const VariationToCopy = {
  [UseCaseVariationEnums.create]: DEFAULT_COPY,
  [UseCaseVariationEnums.collect]: COLLECT_COPY,
  [UseCaseVariationEnums.analyze]: ANALYZE_COPY,
};

export const UseCaseOtherTopBarSlim: React.FC<UseCaseOtherTopBarSlimProps> = ({
  showLoaderOnFetch,
  variation,
}) => {
  const LoaderComponent: React.FC = showLoaderOnFetch
    ? TopBarSlimSkeleton
    : EmptyComponent;

  // Fetch data required for component
  const { loading, error, data } = useQuery(useCaseOtherTopBarSlimQuery);
  const analyzeResponseLimit = data?.user?.limits?.analyzeResponseLimit;
  const createQuestionLimit = data?.user?.limits?.createQuestionLimit;

  const copy =
    variation && variation in VariationToCopy
      ? VariationToCopy[variation]
      : DEFAULT_COPY;
  const ut_source = variation
    ? `slim_${variation}_upsell_other`
    : 'slim_create_upsell_other';

  return (
    <>
      {loading ? (
        <LoaderComponent />
      ) : error || !analyzeResponseLimit || !createQuestionLimit ? (
        <EmptyComponent />
      ) : (
        <TopBarSlim
          message={t(copy.MESSAGE, {
            createQuestionLimit,
            analyzeResponseLimit,
          })}
          ctaText={t(copy.CTA_TEXT)}
          ctaLink={createURL('/pricing/upgrade/', {
            ut_source: 'topbar',
            ut_source2: ut_source,
          })}
          ctaNewTab
        />
      )}
    </>
  );
};

export default UseCaseOtherTopBarSlim;
